import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Building2,
  CreditCard,
  Gift,
  Globe2,
  LineChart,
  PartyPopper,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Wallet,
  Zap,
} from 'lucide-react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const TRACTIONS: { icon: typeof Sparkles; title: string; body: string }[] = [
  {
    icon: BarChart3,
    title: 'Limits from your creator stats, not only a traditional score',
    body: 'We weigh your followers, engagement, GMV and brand deals so your real influence counts—not just an old payslip.',
  },
  {
    icon: TrendingUp,
    title: 'Room to fund bigger campaigns',
    body: 'Creators who qualify can access limits that reflect how you actually earn—so you can scale ad spend, inventory and production.',
  },
  {
    icon: ShieldCheck,
    title: 'Built for eligible creators',
    body: 'Where the programme allows it: no personal guarantee and no hard credit check for qualified creator businesses—approval is based on your creator profile and signals.',
  },
  {
    icon: Sparkles,
    title: 'Rewards where creators spend',
    body: 'Boosted earn on what you already buy: Meta and TikTok ads, gear, merch inventory, travel and more—not only everyday categories.',
  },
  {
    icon: Zap,
    title: 'Turn ad spend into growth',
    body: 'Every ringgit on Meta, TikTok or Google Ads can earn rewards—so your growth budget works harder.',
  },
  {
    icon: Wallet,
    title: 'Bridge lumpy payouts',
    body: 'Smooth cashflow between brand payments and platform settlements: spend now, pay down when the money lands.',
  },
  {
    icon: Globe2,
    title: 'Multi-stream income, one view',
    body: 'Live commerce, brand deals, shops and platform revenue together—not a single salary line.',
  },
  {
    icon: LineChart,
    title: 'Limits that can grow with you',
    body: 'As followers, engagement and GMV rise, your line can increase over time with responsible use.',
  },
  {
    icon: Building2,
    title: 'Serious business, clean books',
    body: 'A dedicated creator-business card so spend is separated from personal—easier for you, your manager and your accountant.',
  },
  {
    icon: PartyPopper,
    title: 'Creator-only perks',
    body: 'Early invites to collabs, tools and events we run with agencies and brands—built for cardholders.',
  },
  {
    icon: CreditCard,
    title: 'Build your business credit story',
    body: 'On-time payments can strengthen your business credit profile over time—start from your real creator revenue.',
  },
  {
    icon: Gift,
    title: 'Built for Asia’s creator stack',
    body: 'TikTok Shop, Shopee, Lazada and cross-border campaigns—with MYR-first thinking and multi-currency where we launch.',
  },
];

const PLATFORMS = ['TikTok', 'YouTube', 'Instagram', 'Shopee', 'Lazada', 'Other'] as const;
const INTERESTS = [
  { id: 'limits', label: 'Higher limits' },
  { id: 'ads', label: 'Ad-spend rewards' },
  { id: 'inventory', label: 'Inventory financing' },
  { id: 'other', label: 'Other' },
] as const;

export default function CreatorCardLanding() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'creator' | 'kol'>('kol');
  const [platforms, setPlatforms] = useState<Record<(typeof PLATFORMS)[number], boolean>>({
    TikTok: false,
    YouTube: false,
    Instagram: false,
    Shopee: false,
    Lazada: false,
    Other: false,
  });
  const [gmvBand, setGmvBand] = useState('');
  const [country, setCountry] = useState('');
  const [interest, setInterest] = useState<Record<(typeof INTERESTS)[number]['id'], boolean>>({
    limits: false,
    ads: false,
    inventory: false,
    other: false,
  });
  const [interestOther, setInterestOther] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const togglePlatform = (p: (typeof PLATFORMS)[number]) => {
    setPlatforms((prev) => ({ ...prev, [p]: !prev[p] }));
  };

  const toggleInterest = (id: (typeof INTERESTS)[number]['id']) => {
    setInterest((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const buildMailBody = () => {
    const selectedPlatforms = PLATFORMS.filter((p) => platforms[p]).join(', ') || '(none selected)';
    const interestParts: string[] = INTERESTS.filter((i) => interest[i.id]).map((i) => i.label);
    if (interest.other && interestOther.trim()) {
      interestParts.push(`Other: ${interestOther.trim()}`);
    }
    const selectedInterests = interestParts.join(', ');
    return [
      'kol.my — Creator card early access',
      '',
      `Email: ${email}`,
      `I am a: ${role === 'kol' ? 'KOL' : 'Creator'}`,
      `Main platforms: ${selectedPlatforms}`,
      `Monthly creator income / GMV: ${gmvBand || '(not selected)'}`,
      `Country: ${country || '(not provided)'}`,
      `Interested in: ${selectedInterests || '(none selected)'}`,
    ].join('\n');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent('Creator card — early access waitlist');
    const body = encodeURIComponent(buildMailBody());
    window.location.href = `mailto:i@kol.my?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-16 pt-24 md:pb-24 md:pt-28">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-4 text-sm font-medium uppercase tracking-widest text-violet-300/90">
            Early access · KOL.my for creators
          </p>
          <h1 className="mb-6 bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-4xl font-bold leading-tight text-transparent md:text-5xl lg:text-6xl">
            Credit designed for creators, not bankers.
          </h1>
          <p className="mx-auto mb-4 max-w-2xl text-lg leading-relaxed text-gray-300 md:text-xl">
            Higher limits, smarter rewards and flexible working capital from your{' '}
            <span className="text-white/95">follower count and GMV</span>—not only a traditional credit
            file.
          </p>
          <p className="mx-auto mb-10 max-w-2xl text-base text-gray-500">
            Join the waitlist for an invite-only creator programme built for KOLs in Asia. We review in
            batches and invite the strongest profiles first.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="#waitlist"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:opacity-95"
            >
              Join early access
              <ArrowRight className="h-5 w-5" aria-hidden />
            </a>
            <Link
              to="/register"
              className="text-sm font-medium text-gray-400 underline-offset-4 transition hover:text-white hover:underline"
            >
              Already on kol.my? Create an account
            </Link>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-16 md:py-20" id="why">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-4 text-center text-3xl font-bold text-white md:text-4xl">
            Why creators join the list
          </h2>
          <p className="mx-auto mb-14 max-w-2xl text-center text-gray-400">
            Twelve reasons we are building a card programme around how you actually earn and spend.
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TRACTIONS.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition hover:border-violet-500/30 hover:bg-white/[0.05]"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300">
                  <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{title}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={1} className="px-6 py-16 md:py-20" id="waitlist">
        <div className="mx-auto max-w-2xl">
          <h2 className="mb-4 text-center text-3xl font-bold text-white md:text-4xl">
            Be part of the first wave in Asia
          </h2>
          <p className="mb-10 text-center text-gray-400">
            If you earn from TikTok, YouTube, Instagram, Shopee, Lazada or brand deals, leave your
            details. We use this to prioritise invites and to share aggregate signals with partners—no
            spam.
          </p>

          {submitted ? (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-10 text-center">
              <p className="mb-2 text-lg font-semibold text-emerald-200">Check your email client</p>
              <p className="text-sm text-gray-400">
                We opened a draft to <span className="text-white">i@kol.my</span>. Send the message to
                confirm your spot. If nothing opened, email us manually with the same details.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm font-medium text-violet-300 underline-offset-2 hover:underline"
              >
                Edit and resend
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-8 rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm"
            >
              <div>
                <label htmlFor="cc-email" className="mb-2 block text-sm font-medium text-gray-300">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  id="cc-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  placeholder="you@example.com"
                />
              </div>

              <fieldset>
                <legend className="mb-3 text-sm font-medium text-gray-300">I am a</legend>
                <div className="flex flex-wrap gap-4">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-3 has-[:checked]:border-violet-500/50 has-[:checked]:bg-violet-500/10">
                    <input
                      type="radio"
                      name="role"
                      checked={role === 'kol'}
                      onChange={() => setRole('kol')}
                      className="text-violet-500 focus:ring-violet-500"
                    />
                    <span className="text-sm text-white">KOL</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-3 has-[:checked]:border-violet-500/50 has-[:checked]:bg-violet-500/10">
                    <input
                      type="radio"
                      name="role"
                      checked={role === 'creator'}
                      onChange={() => setRole('creator')}
                      className="text-violet-500 focus:ring-violet-500"
                    />
                    <span className="text-sm text-white">Creator</span>
                  </label>
                </div>
              </fieldset>

              <fieldset>
                <legend className="mb-3 text-sm font-medium text-gray-300">Main platforms</legend>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => (
                    <label
                      key={p}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm has-[:checked]:border-fuchsia-500/40 has-[:checked]:bg-fuchsia-500/10"
                    >
                      <input
                        type="checkbox"
                        checked={platforms[p]}
                        onChange={() => togglePlatform(p)}
                        className="rounded border-white/20 text-fuchsia-500 focus:ring-fuchsia-500"
                      />
                      <span className="text-gray-200">{p}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label htmlFor="cc-gmv" className="mb-2 block text-sm font-medium text-gray-300">
                  Monthly creator income / GMV (approx.)
                </label>
                <select
                  id="cc-gmv"
                  value={gmvBand}
                  onChange={(e) => setGmvBand(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white focus:border-violet-500/50 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                >
                  <option value="">Select a range</option>
                  <option value="Under RM5k">&lt; RM5,000</option>
                  <option value="RM5k–RM20k">RM5,000 – RM20,000</option>
                  <option value="Over RM20k">&gt; RM20,000</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label htmlFor="cc-country" className="mb-2 block text-sm font-medium text-gray-300">
                  Country / region
                </label>
                <input
                  id="cc-country"
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  placeholder="e.g. Malaysia"
                />
              </div>

              <fieldset>
                <legend className="mb-3 text-sm font-medium text-gray-300">
                  Most interested in
                </legend>
                <div className="flex flex-col gap-2">
                  {INTERESTS.map(({ id, label }) => (
                    <label
                      key={id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 has-[:checked]:border-cyan-500/40 has-[:checked]:bg-cyan-500/10"
                    >
                      <input
                        type="checkbox"
                        checked={interest[id]}
                        onChange={() => toggleInterest(id)}
                        className="rounded border-white/20 text-cyan-500 focus:ring-cyan-500"
                      />
                      <span className="text-sm text-gray-200">{label}</span>
                    </label>
                  ))}
                </div>
                {interest.other && (
                  <input
                    type="text"
                    value={interestOther}
                    onChange={(e) => setInterestOther(e.target.value)}
                    className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                    placeholder="Tell us briefly…"
                  />
                )}
              </fieldset>

              <p className="text-xs leading-relaxed text-gray-500">
                Early access does not guarantee approval. Features, limits and eligibility depend on
                partner bank rules and local regulation. We may contact you about kol.my and this
                programme.
              </p>

              <button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 py-4 text-base font-semibold text-white shadow-lg shadow-violet-500/20 transition hover:opacity-95"
              >
                Join early access
              </button>
            </form>
          )}
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
}
